import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, API_BASE } from "../api";
import { t } from "../i18n";
import { navigate } from "../utils/navigate";

const FREQ = {
  very_common: "veryCommon",
  common: "common",
  uncommon: "uncommon",
  rare: "rare",
};
const ONSET = {
  first_days: "firstDays",
  weeks_later: "weeksLater",
  anytime: "anytime",
};
const SYS = {
  stomach: "stomach",
  skin: "skin",
  sleep: "sleep",
  heart: "heart",
  sugar: "sugar",
  kidneys: "kidneys",
  other: "other",
};

export default function MedicinePage({ lang, rxId }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [showRest, setShowRest] = useState(false);
  const [pick, setPick] = useState(null);
  const [custom, setCustom] = useState("");
  const [severity, setSeverity] = useState("mild");
  const [started, setStarted] = useState(() => new Date().toISOString().slice(0, 10));
  const [msg, setMsg] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let live = true;
    api(`/api/prescriptions/${rxId}`, { token })
      .then((d) => live && setData(d))
      .catch((e) => live && setErr(e.message));
    return () => {
      live = false;
    };
  }, [rxId, token]);

  if (!data && !err) return <p className="tt-page">{t(lang, "loading")}</p>;
  if (err) return <p className="tt-page tt-err">{err}</p>;

  const rx = data.prescription;
  const label = data.label || { pinned: [], rest: [] };
  const copy = data.copy || {};

  const sendTick = async ({ thisIsBad = false, unexpected = false } = {}) => {
    setErr("");
    setMsg("");
    try {
      const body = {
        prescription_id: rx.id,
        severity: thisIsBad ? "emergency" : severity,
        started_on: started,
        still_happening: true,
        this_is_bad: thisIsBad,
      };
      if (unexpected) {
        body.custom_text = custom;
      } else if (pick) {
        body.label_effect_id = pick.id;
      } else if (!thisIsBad) {
        setErr(t(lang, "tickThis"));
        return;
      }
      const res = await api("/api/reports", { method: "POST", token, body });
      setMsg(res.message || copy.notified || t(lang, "notified"));
      setPick(null);
      setCustom("");
      const fresh = await api(`/api/prescriptions/${rxId}`, { token });
      setData(fresh);
    } catch (e) {
      setErr(e.message);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setErr("");
    try {
      const form = new FormData();
      form.append("prescription_id", String(rx.id));
      form.append("file", file);
      await api("/api/packs", { method: "POST", token, form });
      const fresh = await api(`/api/prescriptions/${rxId}`, { token });
      setData(fresh);
    } catch (e) {
      setErr(e.message);
    } finally {
      setUploading(false);
    }
  };

  const EffectCard = ({ fx }) => {
    const serious = fx.seriousness === "emergency" || fx.seriousness === "call_doctor";
    const listed =
      fx.seriousness === "emergency" || fx.seriousness === "call_doctor"
        ? t(lang, "listedSerious")
        : t(lang, "listedCommon");
    return (
      <div className={`tt-fx ${serious ? "serious" : ""}`}>
        <div>
          <strong>{fx.name}</strong>
          <div className="tt-meta">
            <span>{t(lang, FREQ[fx.frequency] || "common")}</span>
            <span>{t(lang, SYS[fx.body_system] || "other")}</span>
            <span>{t(lang, ONSET[fx.onset] || "anytime")}</span>
          </div>
          <p className="tt-call">{fx.call_if || listed}</p>
        </div>
        <button
          className={pick?.id === fx.id ? "tt-btn small on" : "tt-btn small ghost"}
          onClick={() => {
            setPick(fx);
            setSeverity(fx.seriousness === "mild" ? "mild" : fx.seriousness);
          }}
        >
          {t(lang, "tickThis")}
        </button>
      </div>
    );
  };

  return (
    <div className="tt-page">
      <button className="tt-link" onClick={() => navigate("/")}>
        ← {t(lang, "myMedicines")}
      </button>
      <h2>
        {rx.brand} · {rx.salt} {rx.strength}
      </h2>
      <p>
        {rx.dose} · {rx.days} {t(lang, "days").toLowerCase()}
      </p>
      {rx.reason && (
        <p className="tt-muted">
          {t(lang, "why")}: {rx.reason}
        </p>
      )}

      <section className="tt-card">
        <h3>{t(lang, "photoPack")}</h3>
        {data.pack && (
          <img
            className="tt-pack"
            alt=""
            src={`${API_BASE}/api/packs/${data.pack.id}/photo?token=${encodeURIComponent(token)}`}
          />
        )}
        <label className="tt-btn ghost">
          {uploading ? "…" : t(lang, "takePhoto")}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(e) => upload(e.target.files?.[0])}
          />
        </label>
        <p className="tt-hint">
          {t(lang, "confirmSalt")}: {rx.salt} {rx.strength}
        </p>
      </section>

      <section className="tt-col">
        <h3 className="tt-label-h">{t(lang, "labelList")}</h3>
        {(label.pinned || []).map((fx) => (
          <EffectCard key={fx.id} fx={fx} />
        ))}
        {!!(label.rest || []).length && (
          <button className="tt-link" onClick={() => setShowRest((v) => !v)}>
            {showRest ? t(lang, "hideFull") : t(lang, "seeFull")}
          </button>
        )}
        {showRest &&
          (label.rest || []).map((fx) => <EffectCard key={fx.id} fx={fx} />)}
      </section>

      <section className="tt-col lived">
        <h3>{t(lang, "livedList")}</h3>
        {(data.lived || []).length === 0 && (
          <p className="tt-muted">{t(lang, "tickThis")}</p>
        )}
        {(data.lived || []).map((r) => (
          <div key={r.id} className={`tt-lived ${r.is_unexpected ? "red" : ""}`}>
            <strong>{r.name}</strong>
            <span>
              {r.started_on} · {t(lang, r.severity === "emergency" ? "emergency" : r.severity === "call_doctor" ? "callDoctor" : "mild")}
            </span>
            {r.is_unexpected && <span className="tt-chip red">{t(lang, "unexpected")}</span>}
            {!r.is_unexpected && <span className="tt-chip">{t(lang, "expected")}</span>}
            {r.doctor_notified_at && <span className="tt-chip">{t(lang, "notified")}</span>}
          </div>
        ))}
      </section>

      {(pick || custom !== "") && (
        <section className="tt-card tick-box">
          <h3>{pick ? pick.name : t(lang, "somethingElse")}</h3>
          <label>
            {t(lang, "when")}
            <input type="date" value={started} onChange={(e) => setStarted(e.target.value)} />
          </label>
          <p>{t(lang, "howBad")}</p>
          <div className="tt-tabs">
            {["mild", "call_doctor", "emergency"].map((s) => (
              <button
                key={s}
                className={severity === s ? "on" : ""}
                onClick={() => setSeverity(s)}
              >
                {t(lang, s === "call_doctor" ? "callDoctor" : s === "emergency" ? "emergency" : "mild")}
              </button>
            ))}
          </div>
          <button className="tt-btn" onClick={() => sendTick({ unexpected: !pick })}>
            {t(lang, "saveTick")}
          </button>
        </section>
      )}

      <section className="tt-card">
        <h3>{t(lang, "somethingElse")}</h3>
        <textarea
          rows={3}
          placeholder={t(lang, "writeWhat")}
          value={custom}
          onChange={(e) => {
            setCustom(e.target.value);
            setPick(null);
          }}
        />
        <button className="tt-btn ghost" onClick={() => sendTick({ unexpected: true })}>
          {t(lang, "saveTick")}
        </button>
        <button className="tt-btn bad" onClick={() => sendTick({ thisIsBad: true, unexpected: !pick })}>
          {t(lang, "thisIsBad")}
        </button>
      </section>

      {msg && <p className="tt-ok">{msg}</p>}
      {err && <p className="tt-err">{err}</p>}
      <p className="tt-foot">{copy.never_stop || t(lang, "neverStop")}</p>
    </div>
  );
}
